import { useState } from 'react'
import { api } from '../../lib/api.js'

/**
 * Two phases. Normal sign-in, plus the first-sign-in flow: an invited
 * account (no password yet) submits with the password field left blank and
 * is steered into creating one — at least 6 characters with an uppercase
 * letter and a number. autocomplete="username" + "new-password" are what
 * make Chrome and Safari offer to save the new password in the browser's
 * password manager on success.
 */
export default function LoginPage({ onSuccess }) {
  const [phase, setPhase] = useState('login') // login | setup
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const field =
    'tap w-full rounded-xl border border-hairline bg-white px-4 py-3 text-[16px] text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-link'

  async function submitLogin(e) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const res = await api('/auth/login', { body: { email, password } })
      if (res.needsSetup) {
        setPhase('setup')
        setPassword('')
        return
      }
      onSuccess(res.user)
    } catch (err) {
      setError(
        err.status === 401
          ? password
            ? 'Wrong email or password.'
            : 'Wrong email — or this account already has a password.'
          : err.message
      )
    } finally {
      setBusy(false)
    }
  }

  async function submitSetup(e) {
    e.preventDefault()
    if (password !== confirm) {
      setError('Passwords don’t match.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { user } = await api('/auth/setup-password', { body: { email, password } })
      onSuccess(user)
    } catch (err) {
      setError(err.data?.error || err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-grouped px-4">
      <div className="card w-full max-w-sm p-8">
        <div className="mb-6 border-b border-hairline pb-6 text-center">
          {/* the real mark, from cdmstudios.com */}
          <img src="/cdm-logo.png" alt="CDM Sound Studios" className="mx-auto mb-3 h-24 w-auto" />
          <p className="section-header">Company repository</p>
        </div>

        {phase === 'login' ? (
          <form onSubmit={submitLogin} className="space-y-3">
            <input
              type="text"
              required
              autoComplete="username"
              placeholder="Email or username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={field}
            />
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={field}
            />
            <p className="text-xs text-faint">
              First time here? Enter your email, leave the password blank, and sign in.
            </p>
            {error && <p className="text-sm text-danger">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="tap w-full rounded-xl bg-navy py-3 font-medium text-white transition-opacity disabled:opacity-50"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitSetup} className="space-y-3">
            <p className="text-sm text-ink">
              Welcome — <span className="font-medium">{email}</span> doesn’t have a password yet.
              Create one now.
            </p>
            {/* hidden but present: ties the saved password to the email */}
            <input type="text" autoComplete="username" value={email} readOnly className="hidden" />
            <input
              type="password"
              required
              autoFocus
              autoComplete="new-password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={field}
            />
            <input
              type="password"
              required
              autoComplete="new-password"
              placeholder="Repeat it"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={field}
            />
            <p className="text-xs text-faint">
              At least 6 characters, with an uppercase letter and a number. Your browser will
              offer to save it.
            </p>
            {error && <p className="text-sm text-danger">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="tap w-full rounded-xl bg-navy py-3 font-medium text-white transition-opacity disabled:opacity-50"
            >
              {busy ? 'Creating…' : 'Create password & sign in'}
            </button>
            <button
              type="button"
              onClick={() => {
                setPhase('login')
                setPassword('')
                setConfirm('')
                setError(null)
              }}
              className="tap w-full py-1 text-sm text-muted"
            >
              Back
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
